"use client";

import * as React from "react";
import { toast } from "sonner";
import {
  Bot,
  CheckCircle2,
  Copy,
  ImageOff,
  Loader2,
  StickyNote,
  X,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  faDateTime,
  faToman,
  formatCard,
} from "@/lib/format";
import type { AIOrder, AIOrderStatus } from "./types";

type Props = { onRefresh: () => void };
type Filter = "ALL" | AIOrderStatus;

function StatusBadge({ status }: { status: AIOrderStatus }) {
  if (status === "DELIVERED")
    return (
      <Badge className="bg-primary/15 text-primary hover:bg-primary/20">
        تحویل شده
      </Badge>
    );
  if (status === "REJECTED")
    return <Badge variant="destructive">رد شده</Badge>;
  return (
    <Badge className="bg-amber-500/15 text-amber-600 hover:bg-amber-500/20 dark:text-amber-400">
      در انتظار
    </Badge>
  );
}

export default function AiOrdersPanel({ onRefresh }: Props) {
  const [orders, setOrders] = React.useState<AIOrder[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [filter, setFilter] = React.useState<Filter>("ALL");
  const [detail, setDetail] = React.useState<AIOrder | null>(null);

  const [deliverOpen, setDeliverOpen] = React.useState(false);
  const [deliverTarget, setDeliverTarget] = React.useState<AIOrder | null>(null);
  const [accountInfo, setAccountInfo] = React.useState("");
  const [deliverNote, setDeliverNote] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  const [rejectOpen, setRejectOpen] = React.useState(false);
  const [rejectTarget, setRejectTarget] = React.useState<AIOrder | null>(null);
  const [rejectNote, setRejectNote] = React.useState("");
  const [rejecting, setRejecting] = React.useState(false);

  const loadOrders = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/admin/ai-orders", { cache: "no-store" });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || "خطا در دریافت سفارش‌ها");
      }
      setOrders(data.orders ?? []);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "خطا در دریافت سفارش‌ها";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const filtered = React.useMemo(() => {
    return orders.filter((o) => filter === "ALL" || o.status === filter);
  }, [orders, filter]);

  function openDeliver(o: AIOrder) {
    setDetail(null);
    setDeliverTarget(o);
    setAccountInfo(o.accountInfo ?? "");
    setDeliverNote(o.adminNote ?? "");
    setDeliverOpen(true);
  }

  function openReject(o: AIOrder) {
    setDetail(null);
    setRejectTarget(o);
    setRejectNote(o.adminNote ?? "");
    setRejectOpen(true);
  }

  function copyText(text: string, label = "متن کپی شد.") {
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success(label))
      .catch(() => toast.error("کپی ناموفق بود."));
  }

  async function submitDeliver(e: React.FormEvent) {
    e.preventDefault();
    if (!deliverTarget) return;
    if (!accountInfo.trim()) {
      toast.error("اطلاعات اکانت/لینک تحویل را وارد کنید.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/admin/ai-orders", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: deliverTarget.id,
          action: "deliver",
          accountInfo: accountInfo.trim(),
          adminNote: deliverNote.trim() || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "خطا در تحویل سفارش");
      toast.success("سفارش تحویل داده شد.");
      setDeliverOpen(false);
      loadOrders();
      onRefresh();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "خطا در تحویل سفارش";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function submitReject(e: React.FormEvent) {
    e.preventDefault();
    if (!rejectTarget) return;
    if (!rejectNote.trim()) {
      toast.error("برای رد سفارش، دلیل را وارد کنید.");
      return;
    }
    setRejecting(true);
    try {
      const res = await fetch("/api/admin/ai-orders", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: rejectTarget.id,
          action: "reject",
          adminNote: rejectNote.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "خطا در رد سفارش");
      toast.success("سفارش رد شد.");
      setRejectOpen(false);
      loadOrders();
      onRefresh();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "خطا در رد سفارش";
      toast.error(msg);
    } finally {
      setRejecting(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Card className="rounded-2xl border-border/60 shadow-sm">
        <CardHeader className="border-b">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Bot className="size-5 text-primary" />
              سفارش‌های هوش مصنوعی
            </CardTitle>
            <Select
              value={filter}
              onValueChange={(v) => setFilter(v as Filter)}
            >
              <SelectTrigger className="h-9 w-40 text-sm">
                <SelectValue placeholder="همه" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">همه</SelectItem>
                <SelectItem value="PENDING">در انتظار</SelectItem>
                <SelectItem value="DELIVERED">تحویل شده</SelectItem>
                <SelectItem value="REJECTED">رد شده</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full rounded-md" />
              ))}
            </div>
          ) : error ? (
            <div className="py-8 text-center text-sm text-destructive">
              {error}
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              سفارشی یافت نشد.
            </div>
          ) : (
            <div className="max-h-[28rem] overflow-y-auto scrollbar-thin">
              <Table>
                <TableHeader className="sticky top-0 bg-card">
                  <TableRow>
                    <TableHead className="pr-4">کاربر</TableHead>
                    <TableHead>محصول</TableHead>
                    <TableHead>مبلغ</TableHead>
                    <TableHead>کارت فرستنده</TableHead>
                    <TableHead>وضعیت</TableHead>
                    <TableHead>تاریخ</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((o) => (
                    <TableRow
                      key={o.id}
                      className="cursor-pointer"
                      onClick={() => setDetail(o)}
                    >
                      <TableCell className="pr-4">
                        <div className="flex flex-col">
                          <span
                            dir="ltr"
                            className="text-sm font-medium text-right"
                          >
                            {o.user?.phone ?? "—"}
                          </span>
                          {o.user?.name && (
                            <span className="text-xs text-muted-foreground">
                              {o.user.name}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {o.product?.name ?? o.productName ?? "—"}
                      </TableCell>
                      <TableCell className="text-sm font-semibold text-primary">
                        {faToman(o.amountToman)}
                      </TableCell>
                      <TableCell
                        dir="ltr"
                        className="font-mono text-xs text-muted-foreground"
                      >
                        {o.senderCard ? formatCard(o.senderCard) : "—"}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={o.status} />
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                        {faDateTime(o.createdAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <Dialog
        open={!!detail}
        onOpenChange={(o) => !o && setDetail(null)}
      >
        <DialogContent className="max-w-lg rounded-2xl">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Bot className="size-5 text-primary" />
                  جزئیات سفارش هوش مصنوعی
                </DialogTitle>
                <DialogDescription>
                  اطلاعات کامل سفارش و تحویل به کاربر.
                </DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-1 gap-3 rounded-xl border bg-muted/40 p-4 sm:grid-cols-2">
                <DetailField
                  label="کاربر"
                  value={`${detail.user?.name ?? "—"} — ${detail.user?.phone ?? ""}`}
                  dir="ltr"
                />
                <DetailField
                  label="محصول"
                  value={detail.product?.name ?? detail.productName ?? "—"}
                />
                <DetailField
                  label="مبلغ"
                  value={faToman(detail.amountToman)}
                  primary
                />
                <DetailField
                  label="کارت فرستنده"
                  value={detail.senderCard ? formatCard(detail.senderCard) : "—"}
                  dir="ltr"
                  mono
                />
                {detail.trackingCode && (
                  <DetailField
                    label="کد پیگیری"
                    value={detail.trackingCode}
                    dir="ltr"
                    mono
                  />
                )}
                <DetailField
                  label="تاریخ"
                  value={faDateTime(detail.createdAt)}
                />
                {detail.reviewedAt && (
                  <DetailField
                    label="بررسی شده در"
                    value={faDateTime(detail.reviewedAt)}
                  />
                )}
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">وضعیت:</span>
                <StatusBadge status={detail.status} />
              </div>

              {detail.note && (
                <div className="flex items-start gap-2 rounded-lg border bg-muted/40 p-3 text-sm">
                  <StickyNote className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                  <span className="text-muted-foreground">{detail.note}</span>
                </div>
              )}

              {detail.adminNote && (
                <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-xs">
                  <span className="font-medium text-primary">
                    یادداشت ادمین:{" "}
                  </span>
                  <span className="text-muted-foreground">
                    {detail.adminNote}
                  </span>
                </div>
              )}

              {detail.receiptImage ? (
                <a
                  href={detail.receiptImage}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block overflow-hidden rounded-xl border bg-muted/40"
                >
                  <img
                    src={detail.receiptImage}
                    alt="تصویر رسید"
                    className="max-h-60 w-full object-contain"
                  />
                </a>
              ) : (
                <div className="flex h-24 flex-col items-center justify-center gap-2 rounded-xl border bg-muted/40 text-xs text-muted-foreground">
                  <ImageOff className="size-6" />
                  تصویر رسید ثبت نشده
                </div>
              )}

              {detail.status === "DELIVERED" && detail.accountInfo && (
                <div className="rounded-xl border border-primary/30 bg-primary/5 p-3">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-xs font-medium text-primary">
                      اطلاعات اکانت تحویلی
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyText(detail.accountInfo!)}
                    >
                      <Copy className="size-3.5" />
                      کپی
                    </Button>
                  </div>
                  <Textarea
                    dir="ltr"
                    readOnly
                    value={detail.accountInfo}
                    className="min-h-20 bg-background text-left font-mono text-xs"
                  />
                </div>
              )}

              {detail.status === "PENDING" && (
                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <Button
                    variant="outline"
                    className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                    onClick={() => openReject(detail)}
                  >
                    <X className="size-4" />
                    رد
                  </Button>
                  <Button
                    className="bg-primary hover:bg-primary/90"
                    onClick={() => openDeliver(detail)}
                  >
                    <CheckCircle2 className="size-4" />
                    تحویل به کاربر
                  </Button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Deliver dialog */}
      <Dialog
        open={deliverOpen}
        onOpenChange={(o) => !o && setDeliverOpen(false)}
      >
        <DialogContent className="max-w-lg rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-primary">
              <CheckCircle2 className="size-5" />
              تحویل سفارش
            </DialogTitle>
            <DialogDescription>
              {deliverTarget?.user?.phone
                ? `سفارش ${deliverTarget.user.phone} تحویل داده می‌شود.`
                : "این سفارش تحویل داده می‌شود."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitDeliver} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="deliver-account">
                اطلاعات اکانت / لینک تحویل *
              </Label>
              <Textarea
                id="deliver-account"
                dir="ltr"
                placeholder="مثلاً: لینک اکانت یا نام کاربری و رمز عبور"
                value={accountInfo}
                onChange={(e) => setAccountInfo(e.target.value)}
                disabled={submitting}
                className="min-h-28 bg-background text-left font-mono text-xs"
              />
              <p className="text-xs text-muted-foreground">
                این متن به کاربر در پنل نمایش داده می‌شود.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="deliver-note">یادداشت ادمین (اختیاری)</Label>
              <Textarea
                id="deliver-note"
                placeholder="یادداشت داخلی..."
                value={deliverNote}
                onChange={(e) => setDeliverNote(e.target.value)}
                disabled={submitting}
                className="min-h-16 text-sm"
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDeliverOpen(false)}
                disabled={submitting}
              >
                انصراف
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-primary hover:bg-primary/90"
              >
                {submitting ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    در حال ذخیره...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="size-4" />
                    تحویل و ثبت
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog
        open={rejectOpen}
        onOpenChange={(o) => !o && setRejectOpen(false)}
      >
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <X className="size-5" />
              رد سفارش
            </DialogTitle>
            <DialogDescription>
              {rejectTarget?.user?.phone
                ? `سفارش ${rejectTarget.user.phone} رد خواهد شد.`
                : "این سفارش رد خواهد شد."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitReject} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="reject-reason">دلیل رد *</Label>
              <Textarea
                id="reject-reason"
                placeholder="مثلاً: رسید نامعتبر است / مبلغ ناقص است."
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                disabled={rejecting}
                className="min-h-24 text-sm"
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setRejectOpen(false)}
                disabled={rejecting}
              >
                انصراف
              </Button>
              <Button
                type="submit"
                variant="destructive"
                disabled={rejecting}
              >
                {rejecting ? "در حال رد..." : "تأیید رد"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailField({
  label,
  value,
  primary,
  mono,
  dir,
}: {
  label: string;
  value: string;
  primary?: boolean;
  mono?: boolean;
  dir?: "ltr" | "rtl";
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span
        dir={dir}
        className={`text-sm ${mono ? "font-mono text-left" : ""} ${
          primary ? "font-semibold text-primary" : "font-medium"
        }`}
      >
        {value}
      </span>
    </div>
  );
}
